import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { connectSocket, disconnectSocket } from '../socket';
import { registerPush, installPushRefresh } from '../push';
import { playMessageSound, playBuzzSound } from '../utils/sound';
import { setFaviconBadge } from '../utils/favicon';
import * as api from '../api/chat';
import { listCustConversations, custMarkRead } from '../api/customerChat';

// Chuẩn hoá hội thoại KH về cùng "hình dạng" hội thoại nội bộ (id/name/unread/muted) để tái dùng
// nguyên bộ cửa sổ/danh sách. kind='customer' là cờ phân nhánh API + giao diện.
function shapeCustConv(c) {
  return {
    ...c,
    id: c._id,
    kind: 'customer',
    name: c.custName || c.custCode || 'Khách hàng',
    lastMessage: c.lastMessage
      ? { text: (c.lastMessage.direction === 'out' ? 'Bạn: ' : '') + (c.lastMessage.text || ''), at: c.lastMessage.at }
      : null,
  };
}

// Mô tả ngắn cho tin chỉ có đính kèm: "đã gửi 1 hình ảnh / tệp".
function attPreview(text, attachments, sticker, poll) {
  if (text) return text;
  if (sticker) return 'đã gửi 1 sticker';
  if (poll) return `📊 Bình chọn: ${poll.question || ''}`;
  const atts = attachments || [];
  if (!atts.length) return '';
  const img = atts.filter((a) => (a.type || '').startsWith('image/')).length;
  const file = atts.length - img;
  if (img && !file) return `đã gửi ${img} hình ảnh`;
  if (file && !img) return `đã gửi ${file} tệp`;
  return `đã gửi ${atts.length} tệp đính kèm`;
}

const MAX_WINDOWS = 12; // trần AN TOÀN bộ nhớ; số cửa sổ HIỂN THỊ do bề ngang màn hình quyết định (không đè nhau)

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

export function ChatProvider({ children }) {
  const { me } = useAuth();
  const [convs, setConvs] = useState([]);
  const [custConvs, setCustConvs] = useState([]); // hội thoại KHÁCH HÀNG (tab Khách hàng)
  const [tab, setTabRaw] = useState('internal'); // tab panel: 'internal' (Nội bộ) | 'customer' (Khách hàng)
  // ĐỐI TÁC (role DT): KHÔNG có tab Khách hàng/Giám sát — KHOÁ CỨNG ở 'internal'. Mọi setTab('customer')
  // thành no-op với DT -> nội dung KH không bao giờ render (chốt chặn nhiều lớp; backend cũng 403 mọi
  // API /customer-chat cho DT).
  const dtLock = me?.role === 'DT';
  const setTab = (t) => setTabRaw(dtLock ? 'internal' : t);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // panel quản lý: 'list' | 'new' | 'settings'
  const [buzzSignal, setBuzzSignal] = useState(null); // {convId, at} — cửa sổ đúng hội thoại sẽ RUNG
  const [wakeSignal, setWakeSignal] = useState(0); // bump khi tab HIỆN LẠI / socket NỐI LẠI -> khung mở tải bù tin NGAY
  const [windows, setWindows] = useState([]); // các hội thoại đang mở (nhiều cửa sổ)
  const [onlineIds, setOnlineIds] = useState(() => new Set()); // userId đang online (chấm xanh)
  // Bản nháp đang soạn dở theo hội thoại (reactive) — để hiện "Chưa gửi" ở danh sách.
  // Backed localStorage (ft_draft_<convId>) nên còn sau khi F5 / mở lại app.
  const [drafts, setDrafts] = useState(() => {
    const out = {};
    try { for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && k.startsWith('ft_draft_')) { const v = localStorage.getItem(k); if (v && v.trim()) out[k.slice('ft_draft_'.length)] = v; } } } catch { /* bỏ qua */ }
    return out;
  });
  const setDraft = useCallback((convId, text) => {
    const id = String(convId);
    const t = (text || '').trim();
    setDrafts((prev) => {
      if ((prev[id] || '') === t) return prev;
      const next = { ...prev };
      if (t) next[id] = t; else delete next[id];
      return next;
    });
    try { if (t) localStorage.setItem('ft_draft_' + id, text); else localStorage.removeItem('ft_draft_' + id); } catch { /* bỏ qua */ }
  }, []);
  // Cài đặt thông báo (lưu localStorage).
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('ft_chat_sound') !== '0');
  const [notifOn, setNotifOn] = useState(() => localStorage.getItem('ft_chat_notif') !== '0');
  useEffect(() => localStorage.setItem('ft_chat_sound', soundOn ? '1' : '0'), [soundOn]);
  useEffect(() => localStorage.setItem('ft_chat_notif', notifOn ? '1' : '0'), [notifOn]);
  const soundRef = useRef(soundOn);
  const notifRef = useRef(notifOn);
  useEffect(() => {
    soundRef.current = soundOn;
  }, [soundOn]);
  useEffect(() => {
    notifRef.current = notifOn;
  }, [notifOn]);

  const convsRef = useRef([]);
  const custConvsRef = useRef([]);
  const windowsRef = useRef([]);
  const openRef = useRef(false);
  const prevMeIdRef = useRef(null); // theo dõi user đang đăng nhập -> DỌN chat khi đổi tài khoản
  useEffect(() => {
    convsRef.current = convs;
  }, [convs]);
  useEffect(() => {
    custConvsRef.current = custConvs;
  }, [custConvs]);
  useEffect(() => {
    windowsRef.current = windows;
  }, [windows]);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  // Luồng KH đã TẮT thông báo (custMutedSvc) — giữ trong ref để socket handler đọc bản MỚI NHẤT.
  const mutedSvcRef = useRef(me?.custMutedSvc || []);
  useEffect(() => {
    mutedSvcRef.current = me?.custMutedSvc || [];
  }, [me?.custMutedSvc]);

  // Hội thoại đang MỞ TRÊN TRANG "Tin nhắn" (toàn màn) — tính là "đang xem" như cửa sổ widget:
  // tin đến tự đánh dấu đã đọc, không kêu/popup. null = không ở trang đó.
  const pageViewingRef = useRef(null);
  const pageViewing = (convId) => pageViewingRef.current === String(convId) && !document.hidden;

  // Các hội thoại đang THU NHỎ (không tự đánh dấu đã đọc dù cửa sổ vẫn mở).
  const minimizedRef = useRef(new Set());
  const setConvMinimized = useCallback((convId, on) => {
    const k = String(convId);
    if (on) minimizedRef.current.add(k); else minimizedRef.current.delete(k);
  }, []);

  // ĐÁNH DẤU ĐÃ ĐỌC có DỒN (debounce): nhóm đông nhắn dồn dập, trước đây MỖI tin đến -> 1 POST markRead
  // + server phát "đã xem" cho O(thành viên) -> tải nặng lên vô ích. Gom lại 1 lần mỗi ~700ms/hội thoại.
  const markReadTimersRef = useRef({});
  const scheduleMarkRead = useCallback((convId, fn) => {
    const k = String(convId);
    clearTimeout(markReadTimersRef.current[k]);
    markReadTimersRef.current[k] = setTimeout(() => {
      delete markReadTimersRef.current[k];
      fn(k).catch(() => {});
    }, 700);
  }, []);

  // Cập nhật 1 hội thoại ở CẢ danh sách lẫn cửa sổ đang mở (VD sau khi thêm/xoá thành viên nhóm).
  const patchConv = useCallback((convId, patch) => {
    const upd = (c) => (String(c.id) === String(convId) ? { ...c, ...patch } : c);
    setConvs((prev) => prev.map(upd));
    setWindows((prev) => prev.map(upd));
  }, []);

  // Bus tin nhắn: convId -> tập hợp callback của các cửa sổ đang mở.
  const subsRef = useRef(new Map());
  const subscribeMessages = useCallback((convId, fn) => {
    const k = String(convId);
    let set = subsRef.current.get(k);
    if (!set) {
      set = new Set();
      subsRef.current.set(k, set);
    }
    set.add(fn);
    return () => set.delete(fn);
  }, []);

  // Bus "đã xem": convId -> callback của cửa sổ để cập nhật dấu Đã xem.
  const readSubsRef = useRef(new Map());
  const subscribeRead = useCallback((convId, fn) => {
    const k = String(convId);
    let set = readSubsRef.current.get(k);
    if (!set) { set = new Set(); readSubsRef.current.set(k, set); }
    set.add(fn);
    return () => set.delete(fn);
  }, []);

  const custUnread = custConvs.reduce((a, c) => a + (c.unread || 0), 0); // tổng tin KH chưa đọc (badge tab)
  const internalUnread = convs.reduce((a, c) => a + (c.unread || 0), 0); // tổng tin nội bộ chưa đọc (badge tab)
  const totalUnread = internalUnread + custUnread;
  const unreadConvs =
    convs.filter((c) => (c.unread || 0) > 0).length + custConvs.filter((c) => (c.unread || 0) > 0).length; // số cuộc hội thoại có tin mới

  // Hiện số tin mới + biểu tượng loa ngay trên tiêu đề tab trình duyệt.
  const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title || 'FunnyTask' : 'FunnyTask');
  useEffect(() => {
    const base = (baseTitleRef.current || 'FunnyTask').replace(/^🔊.*?•\s*/, '');
    document.title = totalUnread > 0 ? `🔊 (${totalUnread}) Tin nhắn mới • ${base}` : base;
    setFaviconBadge(totalUnread); // chấm đỏ + số trên favicon (giống FB)
    // Số đỏ trên icon app = SỐ TIN CHƯA ĐỌC (iPhone đã cài ra MH chính / macOS) — App Badging API.
    // Ghi luôn con số CHÍNH XÁC vào Cache 'ft-badge' để service worker (lúc app đóng) cộng tiếp
    // từ đây -> badge không còn phình sai theo số thông báo tồn trong khay.
    try {
      if ('setAppBadge' in navigator) {
        if (totalUnread > 0) navigator.setAppBadge(totalUnread);
        else navigator.clearAppBadge();
      }
      if (typeof caches !== 'undefined') {
        caches.open('ft-badge').then((c) => c.put('n', new Response(String(totalUnread)))).catch(() => {});
      }
    } catch { /* trình duyệt không hỗ trợ */ }
  }, [totalUnread]);

  // Popup thông báo trong app ở góc phải.
  const [toasts, setToasts] = useState([]);
  const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
  const pushToast = (t) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev.slice(-3), { ...t, id }]);
    setTimeout(() => dismissToast(id), 6000);
  };

  const refresh = useCallback(() => {
    api.listConversations().then(setConvs).catch(() => {});
  }, []);
  const refreshCust = useCallback(() => {
    listCustConversations().then((list) => setCustConvs((list || []).map(shapeCustConv))).catch(() => {});
  }, []);

  // Đang đứng TRONG trang "Tin nhắn" toàn màn (kể cả chưa chọn hội thoại nào) — trang đó
  // tự là giao diện chat rồi, không bung thêm cửa sổ nổi đè lên.
  const pageActiveRef = useRef(false);
  const setChatPageActive = useCallback((on) => {
    pageActiveRef.current = !!on;
  }, []);

  // TIN MỚI TỰ BUNG CỬA SỔ (kiểu Messenger): khung chat đang mở mà có tin đến ở hội thoại
  // CHƯA mở cửa sổ -> tự mở cửa sổ đó bên cạnh danh sách, khỏi phải bấm vào từng tin.
  // Không bung khi: khung chat đang ĐÓNG (tôn trọng người dùng đã tắt chat — toast lo phần báo),
  // đang ở trang Tin nhắn toàn màn, hoặc hội thoại đã TẮT ÂM (muted = không muốn bị quấy).
  // Tab đang hiện -> cửa sổ bung ra trước mắt = tính "đã xem" (đúng quy ước cửa sổ mở sẵn);
  // tab đang ẨN -> chỉ bung sẵn chờ, GIỮ trạng thái chưa đọc để không "đã xem" oan.
  // QUAN TRỌNG: cửa sổ TỰ BUNG gắn cờ autoOpened -> KHÔNG cướp con trỏ của hội thoại đang gõ
  // (chỉ nhấp nháy báo hiệu kiểu Facebook). Trước đây ô nhập cửa sổ mới tự focus -> gõ nhầm nhóm.
  // Ô soạn tin nào ĐANG được gõ (conv.id) — để KHÔNG tự bung cửa sổ mới đè lên. false = không ai đang gõ.
  const composerFocusedRef = useRef(false);
  const setComposerFocused = useCallback((convId, focused) => {
    if (focused) composerFocusedRef.current = String(convId);
    else if (composerFocusedRef.current === String(convId)) composerFocusedRef.current = false;
  }, []);

  const autoOpenWindow = useCallback((conv) => {
    // Đang GÕ ở 1 ô soạn tin -> KHÔNG tự bung cửa sổ mới. Cửa mới có thể đẩy cửa đang gõ ra khỏi vùng
    // hiển thị (windows.slice(-fit)) khiến ô soạn UNMOUNT giữa chừng -> gãy gõ tiếng Việt (Telex mất dấu).
    if (!openRef.current || pageActiveRef.current || conv.muted || composerFocusedRef.current) return;
    setWindows((prev) => {
      if (prev.some((w) => String(w.id) === String(conv.id))) return prev;
      return [...prev, { ...conv, autoOpened: true }].slice(-MAX_WINDOWS);
    });
    if (!document.hidden) {
      api.markRead(conv.id).catch(() => {});
      setConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    }
  }, []);

  // Báo tin mới khi không mở cửa sổ đúng hội thoại: âm thanh + popup in-app + thông báo OS Chrome
  // (chỉ khi tab đang FOCUS; tab ẩn/đóng thì Web Push lo — tránh HIỆN 2 LẦN).
  const notify = (payload, conv) => {
    if (conv?.muted) return; // hội thoại đã TẮT ÂM -> không kêu, không popup (badge chưa đọc vẫn còn)
    if (pageViewing(payload.conversation)) return; // đang đọc đúng hội thoại này trên trang Tin nhắn
    const viewing = openRef.current && windowsRef.current.some((w) => String(w.id) === String(payload.conversation));
    if (viewing && !document.hidden) return;
    const title = conv?.type === 'group' ? conv.name : payload.sender?.name || 'Tin nhắn mới';
    const preview = attPreview(payload.text, payload.attachments, payload.sticker, payload.poll);
    const body = conv?.type === 'group' ? `${payload.sender?.name}: ${preview}` : preview;
    if (soundRef.current) playMessageSound();
    pushToast({ title, body, convId: String(payload.conversation) });
    // Thông báo OS do WEB PUSH (service worker) lo — server đẩy cho mọi người nhận kể cả khi mở app.
    // Không bắn new Notification in-app nữa để tránh HIỆN 2 LẦN.
  };

  // Kết nối socket + đăng ký push khi đã đăng nhập.
  useEffect(() => {
    const meId = me ? String(me.id || me._id) : null;
    // ĐỔI USER (đăng nhập tài khoản khác / đăng xuất) -> DỌN sạch chat của user CŨ: cửa sổ nổi + danh
    // sách hội thoại + panel. Không dọn thì nhóm/cửa sổ của tài khoản trước còn nằm lại (rỗng nội dung).
    // Chỉ dọn khi meId THỰC SỰ đổi (cập nhật hồ sơ cùng user -> giữ nguyên cửa sổ đang mở).
    if (prevMeIdRef.current !== meId) {
      prevMeIdRef.current = meId;
      setWindows([]);
      setConvs([]);
      setCustConvs([]);
      setOpen(false);
      setView('list');
    }
    if (!me) {
      disconnectSocket();
      return;
    }
    refresh();
    refreshCust();
    registerPush();
    installPushRefresh();
    const socket = connectSocket();
    if (!socket) return;

    const onMessage = (payload) => {
      const convId = String(payload.conversation);
      const mine = String(payload.sender?._id) === String(me.id || me._id);
      const windowOpen = windowsRef.current.some((w) => String(w.id) === convId);

      // Định tuyến tới (các) cửa sổ đang mở hội thoại này.
      const set = subsRef.current.get(convId);
      if (set && set.size) set.forEach((fn) => fn(payload));
      // Đang mở cửa sổ (KHÔNG thu nhỏ) HOẶC đang xem trên trang Tin nhắn + có tin đến -> đã đọc.
      const viewing = (openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId)) || pageViewing(convId);
      if (viewing && !mine) scheduleMarkRead(convId, api.markRead);

      // Hội thoại CHƯA có trong danh sách (VD nhóm mới vừa tạo) -> tải lại danh sách (ngoài updater để
      // React không bỏ qua). Không gọi setConvs cập nhật tại đây vì refresh() sẽ ghi đè toàn bộ.
      // Tải xong cũng TỰ BUNG cửa sổ như hội thoại cũ (người lạ nhắn lần đầu vẫn phải bật ra).
      if (!convsRef.current.some((c) => String(c.id) === convId)) {
        api.listConversations().then((list) => {
          setConvs(list);
          if (!mine) {
            const c = (list || []).find((x) => String(x.id) === convId);
            if (c) autoOpenWindow(c);
          }
        }).catch(() => {});
      } else {
        setConvs((prev) => {
          const idx = prev.findIndex((c) => String(c.id) === convId);
          if (idx === -1) return prev;
          const c = prev[idx];
          const viewingNow = (openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId)) || pageViewing(convId);
          const updated = {
            ...c,
            lastMessage: { text: attPreview(payload.text, payload.attachments, payload.sticker, payload.poll), at: payload.createdAt, sender: payload.sender?._id },
            // Tin hệ thống (ai vào/rời nhóm) cập nhật xem-trước nhưng KHÔNG cộng badge chưa đọc.
            unread: mine || viewingNow ? 0 : (c.unread || 0) + (payload.system ? 0 : 1),
          };
          return [updated, ...prev.filter((_, i) => i !== idx)];
        });
      }

      // Tin hệ thống: không đẩy thông báo OS, không tự bung cửa sổ (chỉ là thông báo trong nhóm).
      if (!mine && !payload.system) {
        const conv = convsRef.current.find((c) => String(c.id) === convId);
        notify(payload, conv);
        if (conv && !windowOpen) autoOpenWindow(conv); // tin đến mà chưa mở cửa sổ -> tự bung bên cạnh
      }
    };

    // Tin CHAT KHÁCH HÀNG (từ webhook bidnhanh hoặc NV khác trả lời) — cập nhật tab Khách hàng.
    const onCustMessage = (payload) => {
      const convId = String(payload.conversation);
      const mine = String(payload.sender?._id || '') === String(me.id || me._id);
      const incoming = payload.direction === 'in'; // KH gửi vào
      const windowOpen = windowsRef.current.some((w) => String(w.id) === convId);
      const set = subsRef.current.get(convId);
      if (set && set.size) set.forEach((fn) => fn(payload));
      const viewing = (openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId)) || pageViewing(convId);
      if (viewing && incoming) scheduleMarkRead(convId, custMarkRead);

      if (!custConvsRef.current.some((c) => String(c.id) === convId)) {
        refreshCust(); // hội thoại KH mới (KH nhắn lần đầu) -> tải danh sách
      } else {
        setCustConvs((prev) => {
          const idx = prev.findIndex((c) => String(c.id) === convId);
          if (idx === -1) return prev;
          const c = prev[idx];
          const updated = {
            ...c,
            lastMessage: { text: attPreview(payload.text, payload.attachments, payload.sticker, payload.poll), at: payload.createdAt },
            unread: !incoming || viewing ? (incoming ? 0 : c.unread || 0) : (c.unread || 0) + 1,
          };
          return [updated, ...prev.filter((_, i) => i !== idx)];
        });
      }

      // Âm + popup: CHỈ khi KH nhắn vào (không báo khi NV khác trả lời) và mình không tắt âm.
      if (incoming && !mine) {
        const conv = custConvsRef.current.find((c) => String(c.id) === convId);
        if (conv?.muted) return;
        // LUỒNG đã TẮT thông báo (custMutedSvc) -> không toast, không âm (vẫn đếm chưa đọc để xem khi cần).
        if (conv && mutedSvcRef.current.includes(conv.svcType)) return;
        const viewingNow = (openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId)) || pageViewing(convId);
        if (viewingNow) return;
        if (soundRef.current) playMessageSound();
        const ttl = `💬 KH: ${conv?.name || payload.senderName || 'Khách hàng'}`;
        const bdy = attPreview(payload.text, payload.attachments, payload.sticker, payload.poll);
        pushToast({ title: ttl, body: bdy, convId, kind: 'customer' });
        // Thông báo OS do WEB PUSH (service worker) lo — không bắn new Notification in-app để khỏi trùng.
      }
    };

    // Chấm xanh: nhận danh sách online lúc kết nối + cập nhật khi có người vào/ra.
    const onPresenceList = (ids) => setOnlineIds(new Set((ids || []).map(String)));
    const onPresenceUpdate = ({ userId, online }) => setOnlineIds((prev) => {
      const next = new Set(prev);
      if (online) next.add(String(userId)); else next.delete(String(userId));
      return next;
    });
    // "Đã xem": chuyển tới cửa sổ hội thoại tương ứng.
    const onRead = (evt) => {
      const set = readSubsRef.current.get(String(evt.conversation));
      if (set && set.size) set.forEach((fn) => fn(evt));
    };

    // Người gửi bấm 🔔 "nhắc lại" -> kêu + popup trong app cho người được tag (kể cả đang mở app).
    // KHÔNG tạo Notification ở đây: Web Push (service worker) đã lo thông báo hệ thống -> tránh hiện 2 lần.
    const onRemind = (e) => {
      if (soundRef.current) playMessageSound();
      pushToast({ title: e.title || '🔔 Nhắc phản hồi', body: e.body || '', convId: String(e.conversation) });
    };

    const onConversation = () => refresh();
    // Hội thoại KH có thay đổi phụ (công việc chat KH thêm/hoàn thành/xoá…) -> tải lại danh sách KH
    // để panel cập nhật tasks. Nhẹ, chỉ 1 lần/thay đổi.
    const onCustConversation = () => refreshCust();
    // NHÓM BỊ XOÁ (người tạo nhóm xoá) -> gỡ khỏi danh sách + đóng cửa sổ đang mở ngay.
    const onConvRemoved = (e) => {
      const id = String(e?.id || '');
      if (!id) return;
      setConvs((prev) => prev.filter((c) => String(c.id) !== id));
      setWindows((prev) => prev.filter((w) => String(w.id) !== id));
      // Báo trang Tin nhắn (cột giữa) rời hội thoại nếu đang mở đúng nhóm vừa xoá.
      window.dispatchEvent(new CustomEvent('ft:conv-removed', { detail: { id } }));
    };
    // Khi (re)kết nối — kể cả sau khi backend chập chờn/khởi động lại — tự đồng bộ lại
    // danh sách hội thoại (khỏi phải F5). Bỏ qua lần connect đầu (đã refresh() ở trên).
    let firstConnect = true;
    const onConnect = () => {
      if (firstConnect) { firstConnect = false; return; }
      refresh();
      refreshCust();               // đồng bộ CẢ danh sách KH (trước chỉ refresh nội bộ -> tin KH trễ tới poll 20s)
      setWakeSignal((n) => n + 1); // khung đang mở tải bù tin NGAY (không đợi tick 2.5-5s)
    };
    socket.on('connect', onConnect);
    socket.on('chat:message', onMessage);
    socket.on('custchat:message', onCustMessage);
    socket.on('chat:conversation', onConversation);
    socket.on('custchat:conversation', onCustConversation);
    socket.on('chat:conversation-removed', onConvRemoved);
    socket.on('presence:list', onPresenceList);
    socket.on('presence:update', onPresenceUpdate);
    socket.on('chat:read', onRead);
    socket.on('chat:remind', onRemind);
    // Thông báo chung (giao việc, đề xuất, lương…): đẩy sang NotificationContext qua window event.
    const onNotify = (d) => window.dispatchEvent(new CustomEvent('ft:notify', { detail: d || {} }));
    socket.on('notify', onNotify);

    // BUZZ! nhận được: kêu âm rung + bung cửa sổ đúng hội thoại (để thấy hiệu ứng rung) + toast.
    const onBuzz = (evt) => {
      const convId = String(evt.conversation);
      const conv = convsRef.current.find((c) => String(c.id) === convId);
      if (conv?.muted) return; // hội thoại đã tắt âm -> không rung, không kêu
      playBuzzSound();
      setOpen(true);
      // KHÔNG bung thêm cửa sổ khi đang gõ ở ô khác (tránh đẩy cửa đang gõ ra -> gãy gõ tiếng Việt).
      if (conv && !composerFocusedRef.current) {
        setWindows((prev) => (prev.some((w) => String(w.id) === convId) ? prev : [...prev, { ...conv, autoOpened: true }].slice(-MAX_WINDOWS)));
      }
      setBuzzSignal({ convId, at: Date.now() }); // cửa sổ tương ứng sẽ RUNG (kể cả vừa mới bung)
      // Buzz từ NHÓM -> hiện tên nhóm để biết rung từ đâu (convName do server gửi; dự phòng lấy từ conv).
      const gname = evt.convName || (conv?.type === 'group' ? conv.name : '');
      pushToast({ title: gname ? `🐝 BUZZ!!! · ${gname}` : '🐝 BUZZ!!!', body: `${evt.name || 'Ai đó'} rung chuông gọi bạn`, convId });
    };
    socket.on('chat:buzz', onBuzz);
    // NHÓM GIÁM SÁT (GĐ đã BẬT 🔔, KHÔNG phải thành viên): tin mới -> CHỈ hiện toast. TUYỆT ĐỐI
    // không markRead / không cập nhật danh sách -> giữ nguyên tắc "giám sát không để lại dấu vết".
    const onWatch = (payload) => {
      if (String(payload.sender?._id) === String(me.id || me._id)) return;
      if (soundRef.current) playMessageSound();
      pushToast({
        title: '👁 ' + (payload.watchGroupName || 'Nhóm giám sát'),
        body: (payload.sender?.name ? payload.sender.name + ': ' : '') + attPreview(payload.text, payload.attachments, payload.sticker, payload.poll),
        convId: String(payload.conversation),
        kind: 'watch',
      });
    };
    socket.on('chat:watch', onWatch);
    return () => {
      socket.off('connect', onConnect);
      socket.off('chat:message', onMessage);
      socket.off('chat:watch', onWatch);
      socket.off('custchat:message', onCustMessage);
      socket.off('chat:conversation', onConversation);
      socket.off('custchat:conversation', onCustConversation);
      socket.off('chat:conversation-removed', onConvRemoved);
      socket.off('presence:list', onPresenceList);
      socket.off('presence:update', onPresenceUpdate);
      socket.off('chat:read', onRead);
      socket.off('chat:remind', onRemind);
      socket.off('notify', onNotify);
      socket.off('chat:buzz', onBuzz);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // DỰ PHÒNG khi socket chập chờn: cứ ~20s làm mới danh sách hội thoại (tin cuối + số chưa đọc)
  // để sidebar tự cập nhật mà khỏi F5, kể cả khi realtime không tới.
  useEffect(() => {
    if (!me) return;
    const t = setInterval(() => { if (!document.hidden) { refresh(); refreshCust(); } }, 20000);
    return () => clearInterval(t);
  }, [me, refresh, refreshCust]);

  // TAB HIỆN LẠI / FOCUS: tải bù NGAY (danh sách + khung mở) — không đợi poll/tick. Bịt dứt điểm cảnh
  // "thông báo đến rồi mà nội dung tin chưa hiện": người dùng bấm thông báo quay lại là thấy tin liền.
  useEffect(() => {
    if (!me) return;
    let last = 0;
    const wake = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - last < 1200) return; // gộp visibilitychange + focus bắn sát nhau
      last = now;
      refresh();
      refreshCust();
      setWakeSignal((n) => n + 1); // khung mở tải bù tin ngay
    };
    document.addEventListener('visibilitychange', wake, { passive: true });
    window.addEventListener('focus', wake, { passive: true });
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [me, refresh, refreshCust]);

  // Mở 1 hội thoại thành 1 cửa sổ. Nếu ĐÃ mở thì ĐƯA LÊN TRƯỚC (cuối mảng = mới nhất) —
  // màn hẹp chỉ hiện được ít cửa sổ, không đưa lên trước thì bấm vào hội thoại đang bị che
  // sẽ không thấy gì xảy ra. Tối đa MAX_WINDOWS.
  const openConv = useCallback((conv) => {
    setOpen(true);
    setWindows((prev) => {
      const exist = prev.find((w) => String(w.id) === String(conv.id));
      if (exist) {
        // Người dùng CHỦ ĐỘNG mở -> bỏ cờ tự-bung (hết nhấp nháy, được phép nhận con trỏ).
        const picked = exist.autoOpened ? { ...exist, autoOpened: false } : exist;
        if (prev[prev.length - 1] === exist && picked === exist) return prev;
        return [...prev.filter((w) => w !== exist), picked];
      }
      return [...prev, conv].slice(-MAX_WINDOWS);
    });
    // CHẾ ĐỘ GIÁM SÁT (GD xem nhóm không có mình): TUYỆT ĐỐI không markRead / đụng gì —
    // thành viên nhóm không được thấy dấu vết nào.
    if (conv.observer) return;
    // Đánh dấu đã đọc theo ĐÚNG loại hội thoại (nội bộ / khách hàng).
    if (conv.kind === 'customer') {
      custMarkRead(conv.id).catch(() => {});
      setCustConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    } else {
      api.markRead(conv.id).catch(() => {});
      setConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    }
  }, []);

  const closeWindow = useCallback((convId) => {
    setWindows((prev) => prev.filter((w) => String(w.id) !== String(convId)));
  }, []);

  // Cửa sổ nổi (widget): ĐỔI KÊNH của cùng khách = THAY nội dung cửa sổ hiện tại tại chỗ,
  // KHÔNG mở thêm cửa sổ mới (tránh 1 khách bung 2–3 cửa sổ khi bấm tab kênh).
  const switchWindowConv = useCallback((oldId, conv) => {
    setWindows((prev) => {
      // Kênh mới đã mở sẵn thành cửa sổ khác -> đóng cửa cũ, đưa cửa kênh mới lên trước.
      if (prev.some((w) => String(w.id) === String(conv.id))) {
        return [...prev.filter((w) => String(w.id) !== String(oldId) && String(w.id) !== String(conv.id)), conv];
      }
      return prev.map((w) => (String(w.id) === String(oldId) ? conv : w)); // thay tại chỗ
    });
    if (conv.kind === 'customer') {
      custMarkRead(conv.id).catch(() => {});
      setCustConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    }
  }, []);

  // Trang "Tin nhắn" chọn 1 hội thoại để xem (KHÔNG mở cửa sổ nổi). conv=null khi rời trang.
  // Giám sát (observer) thì không markRead — giữ đúng nguyên tắc không để lại dấu vết.
  const selectPageConv = useCallback((conv) => {
    pageViewingRef.current = conv ? String(conv.id) : null;
    if (!conv || conv.observer) return;
    if (conv.kind === 'customer') {
      custMarkRead(conv.id).catch(() => {});
      setCustConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    } else {
      api.markRead(conv.id).catch(() => {});
      setConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
    }
  }, []);

  // Mở hội thoại theo id (dùng khi bấm vào popup thông báo). kind='customer' -> tìm bên tab KH.
  const openConvById = useCallback(
    (id, kind) => {
      setOpen(true);
      if (kind === 'customer') {
        setTab('customer');
        const c = custConvsRef.current.find((x) => String(x.id) === String(id));
        if (c) openConv(c);
        else refreshCust();
        return;
      }
      const c = convsRef.current.find((x) => String(x.id) === String(id));
      if (c) openConv(c);
      else refresh();
    },
    [openConv, refresh, refreshCust]
  );

  // MỞ SÂU theo id (từ bấm thông báo): nếu hội thoại chưa có trong danh sách (app vừa mở nguội),
  // ghi nhớ id rồi tải danh sách; useEffect bên dưới sẽ mở khi danh sách về. Không để "trôi" mất tin.
  const pendingOpenRef = useRef(null);
  // Bấm THÔNG BÁO -> mở hội thoại Ở TRANG "Tin nhắn" (ChatPage tự setSel), KHÔNG mở cửa sổ dock nổi.
  // Dock bị ẩn trên mobile nên trước đây bấm thông báo chỉ ra màn DANH SÁCH, không vào chi tiết.
  // deepConv = hội thoại cần mở; ChatPage đọc + setSel; App nghe 'ft:open-conv-page' để chuyển sang trang Tin nhắn.
  const deepNonceRef = useRef(0);
  const [deepConv, setDeepConv] = useState(null);
  const clearDeepConv = useCallback(() => setDeepConv(null), []);
  const routeConvToPage = useCallback((c, kind) => {
    setDeepConv({ conv: c, kind: kind || (c.kind === 'customer' ? 'customer' : undefined), nonce: ++deepNonceRef.current });
    // Bắn SAU 1 nhịp: mở NGUỘI từ thông báo thì effect con (Provider) chạy trước effect cha (App)
    // -> App chưa kịp gắn listener. Hoãn 1 tick để App nhận được rồi chuyển sang trang Tin nhắn.
    setTimeout(() => { try { window.dispatchEvent(new CustomEvent('ft:open-conv-page')); } catch (e) { /* bỏ qua */ } }, 0);
  }, []);
  const deepOpenConv = useCallback(
    (id, kind) => {
      if (!id) return;
      const list = kind === 'customer' ? custConvsRef.current : convsRef.current;
      const c = list.find((x) => String(x.id) === String(id));
      if (c) { pendingOpenRef.current = null; routeConvToPage(c, kind); }
      else { pendingOpenRef.current = { id: String(id), kind }; if (kind === 'customer') refreshCust(); else refresh(); }
    },
    [routeConvToPage, refresh, refreshCust]
  );
  // Danh sách vừa cập nhật -> nếu đang chờ mở 1 hội thoại (bấm thông báo lúc mở nguội) thì mở ngay.
  useEffect(() => {
    const p = pendingOpenRef.current;
    if (!p) return;
    const list = p.kind === 'customer' ? custConvs : convs;
    const c = list.find((x) => String(x.id) === String(p.id));
    if (c) { pendingOpenRef.current = null; routeConvToPage(c, p.kind); }
  }, [convs, custConvs, routeConvToPage]);

  // ĐIỀU HƯỚNG KHI BẤM THÔNG BÁO — 3 nguồn:
  //  (a) URL ?conv=/?cconv= (service worker mở nguội bằng openWindow),
  //  (b) sự kiện 'nativePushTap' + sessionStorage (app Capacitor Android/iOS),
  //  (c) postMessage 'focus-conv' từ service worker (web, khi đã có cửa sổ).
  useEffect(() => {
    if (!me) return;
    try {
      const u = new URL(window.location.href);
      const cv = u.searchParams.get('conv');
      const cc = u.searchParams.get('cconv');
      if (cv || cc) {
        deepOpenConv(cv || cc, cc ? 'customer' : undefined);
        u.searchParams.delete('conv'); u.searchParams.delete('cconv');
        window.history.replaceState({}, '', u.pathname + (u.search ? u.search : '') + u.hash);
      }
    } catch (e) { /* URL lạ -> bỏ qua */ }

    const onNativeTap = (e) => {
      const dt = e.detail || {};
      if (dt.convId) deepOpenConv(dt.convId);
      else if (dt.custConvId) deepOpenConv(dt.custConvId, 'customer');
    };
    window.addEventListener('nativePushTap', onNativeTap);
    // Cold start: app mở lại từ thông báo native trước khi listener kịp gắn -> đọc lại từ sessionStorage.
    try {
      const raw = sessionStorage.getItem('nativePushTap');
      if (raw) { sessionStorage.removeItem('nativePushTap'); onNativeTap({ detail: JSON.parse(raw) }); }
    } catch (e) { /* bỏ qua */ }

    const onSwMsg = (e) => {
      const dt = e.data || {};
      if (dt && dt.type === 'focus-conv') {
        if (dt.convId) deepOpenConv(dt.convId);
        else if (dt.custConvId) deepOpenConv(dt.custConvId, 'customer');
      }
    };
    navigator.serviceWorker && navigator.serviceWorker.addEventListener && navigator.serviceWorker.addEventListener('message', onSwMsg);
    return () => {
      window.removeEventListener('nativePushTap', onNativeTap);
      navigator.serviceWorker && navigator.serviceWorker.removeEventListener && navigator.serviceWorker.removeEventListener('message', onSwMsg);
    };
  }, [me, deepOpenConv]);

  // Sau khi MÌNH gửi: đưa hội thoại lên đầu danh sách + cập nhật tin cuối.
  const bumpConv = useCallback((convId, msg) => {
    setConvs((prev) => {
      const idx = prev.findIndex((c) => String(c.id) === String(convId));
      if (idx === -1) return prev;
      const updated = {
        ...prev[idx],
        lastMessage: { text: attPreview(msg.text, msg.attachments, msg.sticker, msg.poll), at: msg.createdAt, sender: msg.sender?._id },
      };
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  }, []);

  // Sau khi MÌNH trả lời KH: đưa hội thoại KH lên đầu + cập nhật tin cuối.
  const bumpCustConv = useCallback((convId, msg) => {
    setCustConvs((prev) => {
      const idx = prev.findIndex((c) => String(c.id) === String(convId));
      if (idx === -1) return prev;
      const updated = { ...prev[idx], lastMessage: { text: attPreview(msg.text, msg.attachments, msg.sticker, msg.poll), at: msg.createdAt }, unread: 0 };
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  }, []);

  // Cập nhật 1 hội thoại KH ở cả danh sách lẫn cửa sổ đang mở (VD sau khi tắt âm).
  const patchCustConv = useCallback((convId, patch) => {
    const upd = (c) => (String(c.id) === String(convId) ? { ...c, ...patch } : c);
    setCustConvs((prev) => prev.map(upd));
    setWindows((prev) => prev.map(upd));
  }, []);

  // XOÁ (ẨN) hội thoại khỏi danh sách phía client (sau khi gọi API hide). Đóng luôn cửa sổ nổi nếu đang mở.
  // Khi có TIN MỚI, handler realtime không thấy conv trong list -> tự refetch -> hội thoại hiện lại.
  const removeConvsLocal = useCallback((ids, kind) => {
    const del = new Set((ids || []).map(String));
    if (kind === 'customer') setCustConvs((prev) => prev.filter((c) => !del.has(String(c.id))));
    else setConvs((prev) => prev.filter((c) => !del.has(String(c.id))));
    setWindows((prev) => prev.filter((w) => !del.has(String(w.id))));
  }, []);

  const startDirect = async (userId) => {
    const conv = await api.openDirect(userId);
    setConvs((prev) => (prev.some((c) => String(c.id) === String(conv.id)) ? prev : [conv, ...prev]));
    setView('list');
    openConv(conv);
  };

  const startGroup = async (payload) => {
    const conv = await api.createGroup(payload);
    setConvs((prev) => [conv, ...prev]);
    setView('list');
    openConv(conv);
  };

  const value = {
    me,
    convs,
    custConvs,
    tab: dtLock ? 'internal' : tab, // DT luôn ở tab Nội bộ (không lộ tab/nội dung KH)
    setTab,
    custUnread,
    internalUnread,
    refreshCust,
    bumpCustConv,
    patchCustConv,
    removeConvsLocal,
    selectPageConv,
    setChatPageActive,
    totalUnread,
    unreadConvs,
    soundOn,
    setSoundOn,
    notifOn,
    setNotifOn,
    open,
    setOpen,
    view,
    setView,
    buzzSignal,
    wakeSignal,
    windows,
    refresh,
    openConv,
    openConvById,
    deepConv,
    clearDeepConv,
    closeWindow,
    switchWindowConv,
    subscribeMessages,
    subscribeRead,
    setConvMinimized,
    patchConv,
    onlineIds,
    bumpConv,
    drafts,
    setDraft,
    setComposerFocused,
    toasts,
    dismissToast,
    startDirect,
    startGroup,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
